import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Badge, Divider, Menu, MenuItem, Collapse } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { styled } from '@mui/material/styles';
import AppsIcon from '@mui/icons-material/Apps';
import { useSelector } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { STRINGS, REGEX } from '../../constants';
import { Account, DPDLogo, ShoppingCartIcon } from '../icons';
import { useAuth } from '../../features/Auth/Context';
import { MAIN_MENU } from '../../constants/analytics';
import useAnalytics from '../../hooks/useAnalytics';
import {
  BASKET,
  FAQ as FAQ_PATH,
  HELP as HELP_PATH,
  PICKUP_FINDER as PICKUP_FINDER_PATH,
  PROFILE,
  QUOTES,
  SIGN_IN_WITH_EMAIL,
  TRACK as TRACK_PATH,
  // eslint-disable-next-line no-unused-vars
  PRODUCTS_AND_SERVICES as PRODUCTS_AND_SERVICES_PATH,
  ORDER,
} from '../../constants/routes';
import { BasketSelectors } from '../../redux/basket';
import useBreakpoint from '../../hooks/useBreakpoint';
import ProductsAndServicesMenu from './ProductsAndServicesMenu';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.vars.palette.text.primary,
  zIndex: 3,
}));

const StyledLogoText = styled(Typography)(({ theme }) => ({
  textTransform: 'none',
  color: theme.vars.palette.text.primary,
}));

const StyledNavigationButton = styled(Button)(({ theme, isactive }) => ({
  color: isactive ? theme.palette.primary.main : 'inherit',
  textTransform: 'none',
}));

StyledNavigationButton.propTypes = {
  isactive: PropTypes.bool,
};

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const HREF_ATTRIBUTE = 'href';

function HomeAppBar() {
  const { currentSession, signOut } = useAuth();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [productsAndServicesEl, setProductsAndServicesEl] = useState(null);
  const [productsAndServicesExpanded, setProductsAndServicesExpanded] =
    useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const count = useSelector(BasketSelectors.getBasketItemsCount);
  const { trackAction, Trackable } = useAnalytics();
  const isSmallScreen = useBreakpoint('md');

  const handleOpenNavMenu = event => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenProductsAndServicesMenu = event => {
    setProductsAndServicesEl(event.currentTarget);
  };

  const handleCloseProductsAndServicesMenu = () => {
    setProductsAndServicesEl(null);
  };

  const onMenuItemClick = useCallback(
    event => {
      event.preventDefault();
      handleCloseNavMenu();
      handleCloseProductsAndServicesMenu();
      trackAction(event);
      const href = event.currentTarget.getAttribute(HREF_ATTRIBUTE);
      if (href) {
        navigate(href);
      }
    },
    [navigate, trackAction]
  );

  const onSignOutClick = useCallback(
    async event => {
      event.preventDefault();
      handleCloseNavMenu();
      trackAction(event);

      // Signout
      signOut();
    },
    [signOut, trackAction]
  );

  return (
    <Trackable loadId={MAIN_MENU.LOAD}>
      <StyledAppBar position={isSmallScreen ? 'sticky' : 'static'}>
        <Toolbar>
          <nav style={{ display: 'contents' }}>
            <Box sx={{ flexGrow: { xs: 1, md: 0 } }}>
              <Button href='/' onClick={onMenuItemClick}>
                <DPDLogo sx={{ fontSize: 52 }} />
                <Divider
                  orientation='vertical'
                  sx={{
                    mx: 1,
                    borderRightWidth: 2,
                    height: 26,
                    alignSelf: 'center',
                  }}
                />
                <StyledLogoText noWrap>{STRINGS.DPD_ONLINE}</StyledLogoText>
              </Button>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                alignItems: 'center',
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
              }}
            >
              <StyledNavigationButton
                isactive={pathname === ORDER}
                actionid={MAIN_MENU.CLICK_SEND}
                id={MAIN_MENU.CLICK_SEND}
                href={QUOTES}
                onClick={onMenuItemClick}
              >
                {STRINGS.SEND}
              </StyledNavigationButton>
              <StyledNavigationButton
                isactive={pathname === TRACK_PATH}
                actionid={MAIN_MENU.CLICK_TRACK}
                id={MAIN_MENU.CLICK_TRACK}
                href={TRACK_PATH}
                onClick={onMenuItemClick}
              >
                {STRINGS.TRACK}
              </StyledNavigationButton>
              <StyledNavigationButton
                isactive={REGEX.PRODUCTS_AND_SERVICES_PAGES.test(pathname)}
                onClick={handleOpenProductsAndServicesMenu}
                actionid={MAIN_MENU.CLICK_PRODUCTS_AND_SERVICES}
                id={'productsAndServices'}
                aria-haspopup='true'
              >
                {STRINGS.PRODUCTS_AND_SERVICES}
              </StyledNavigationButton>
              <Menu
                id='menu-productsAndServices'
                open={Boolean(productsAndServicesEl)}
                anchorEl={productsAndServicesEl}
                onClose={handleCloseProductsAndServicesMenu}
                sx={{ top: '14px', display: { xs: 'none', md: 'block' } }}
              >
                <ProductsAndServicesMenu onMenuItemClick={onMenuItemClick} />
              </Menu>
              <StyledNavigationButton
                isactive={pathname === PICKUP_FINDER_PATH}
                actionid={MAIN_MENU.CLICK_PICKUPFINDER}
                id={MAIN_MENU.CLICK_PICKUPFINDER}
                href={PICKUP_FINDER_PATH}
                onClick={onMenuItemClick}
              >
                {STRINGS.PICKUP_FINDER}
              </StyledNavigationButton>
              <StyledNavigationButton
                isactive={pathname === HELP_PATH}
                actionid={MAIN_MENU.CLICK_HELP}
                id={'Help'}
                href={HELP_PATH}
                onClick={onMenuItemClick}
              >
                {STRINGS.HELP}
              </StyledNavigationButton>
              <StyledNavigationButton
                isactive={pathname === FAQ_PATH}
                actionid={MAIN_MENU.CLICK_FAQS}
                id={'FAQs'}
                href={FAQ_PATH}
                onClick={onMenuItemClick}
              >
                {STRINGS.FAQS}
              </StyledNavigationButton>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'block' } }}>
              <StyledIconButton
                size='large'
                aria-label={`Shopping cart with ${count} items`}
                color='inherit'
                href={BASKET}
                role='button'
                actionid={MAIN_MENU.CLICK_BASKET}
                id={MAIN_MENU.CLICK_BASKET}
                onClick={onMenuItemClick}
              >
                <Badge badgeContent={count} color='error'>
                  <ShoppingCartIcon sx={{ fontSize: 36 }} />
                </Badge>
              </StyledIconButton>
              {!currentSession?.uid && (
                <Button
                  color='inherit'
                  href={SIGN_IN_WITH_EMAIL}
                  actionid={MAIN_MENU.CLICK_SIGN_IN}
                  onClick={onMenuItemClick}
                >
                  {STRINGS.SIGN_IN}
                </Button>
              )}
              {currentSession?.uid && (
                <>
                  <StyledIconButton
                    size='large'
                    aria-label='user preferences'
                    color='inherit'
                    href={PROFILE}
                    actionid={MAIN_MENU.CLICK_ACCOUNT}
                    onClick={onMenuItemClick}
                  >
                    <Account sx={{ fontSize: 36 }} />
                  </StyledIconButton>
                  <StyledIconButton
                    size='large'
                    aria-label='sign out'
                    actionid={MAIN_MENU.CLICK_SIGN_OUT}
                    onClick={onSignOutClick}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                    }}
                  >
                    <LogoutIcon />
                  </StyledIconButton>
                </>
              )}
              <Box
                sx={{ flexGrow: 1, display: { xs: 'inline-flex', md: 'none' } }}
              >
                <StyledIconButton
                  size='large'
                  aria-label='home'
                  color='inherit'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  onClick={handleOpenNavMenu}
                >
                  <AppsIcon />
                </StyledIconButton>
                <Menu
                  id='menu-appbar'
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                  }}
                >
                  <MenuItem
                    key='send'
                    href={QUOTES}
                    actionid={MAIN_MENU.CLICK_SEND}
                    onClick={onMenuItemClick}
                  >
                    <Typography textAlign='center'>{STRINGS.SEND}</Typography>
                  </MenuItem>
                  <MenuItem
                    key='track'
                    actionid={MAIN_MENU.CLICK_TRACK}
                    href={TRACK_PATH}
                    onClick={onMenuItemClick}
                  >
                    <Typography textAlign='center'>{STRINGS.TRACK}</Typography>
                  </MenuItem>
                  <MenuItem
                    key='productsAndServices'
                    onClick={() =>
                      setProductsAndServicesExpanded(
                        !productsAndServicesExpanded
                      )
                    }
                    actionid={MAIN_MENU.CLICK_PRODUCTS_AND_SERVICES}
                  >
                    <Typography textAlign='center'>
                      {STRINGS.PRODUCTS_AND_SERVICES}
                    </Typography>
                    {productsAndServicesExpanded ? (
                      <ArrowDropUpIcon sx={{ ml: 'auto' }} />
                    ) : (
                      <ArrowDropDownIcon sx={{ ml: 'auto' }} />
                    )}
                  </MenuItem>
                  <Collapse in={productsAndServicesExpanded}>
                    <ProductsAndServicesMenu
                      onMenuItemClick={onMenuItemClick}
                    />
                  </Collapse>
                  <MenuItem
                    key='pickupfinder'
                    actionid={MAIN_MENU.CLICK_PICKUPFINDER}
                    href={PICKUP_FINDER_PATH}
                    onClick={onMenuItemClick}
                  >
                    <Typography textAlign='center'>
                      {STRINGS.PICKUP_FINDER}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    key='help'
                    actionid={MAIN_MENU.CLICK_HELP}
                    href={HELP_PATH}
                    onClick={onMenuItemClick}
                  >
                    <Typography textAlign='center'>{STRINGS.HELP}</Typography>
                  </MenuItem>
                  <MenuItem
                    key='faq'
                    actionid={MAIN_MENU.CLICK_FAQS}
                    href={FAQ_PATH}
                    onClick={onMenuItemClick}
                  >
                    <Typography textAlign='center'>{STRINGS.FAQS}</Typography>
                  </MenuItem>
                  {currentSession?.uid && (
                    <MenuItem
                      key='signout'
                      aria-label='sign out'
                      actionid={MAIN_MENU.CLICK_SIGN_OUT}
                      onClick={onSignOutClick}
                    >
                      <Typography textAlign='center'>
                        {STRINGS.SIGN_OUT}
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>
          </nav>
        </Toolbar>
      </StyledAppBar>
    </Trackable>
  );
}

export default HomeAppBar;
